<template>
  <div class="synthese__container">
    <SyntheseSection
      title="Périodes attestées et coûts pédagogiques"
      :items="periodesCoutsStagiaireDetails"
      style="width: 100vw"
    >
      <template #header>
        <div class="element__header">
          <BasicText
            size="body-m"
            color="secondary-900"
            weight="bold"
            wrap
          >
            Prestataire
          </BasicText>
          <BasicText
            size="body-m"
            color="secondary-900"
            weight="bold"
            wrap
          >
            Stagiaire
          </BasicText>
          <BasicText
            size="body-m"
            color="secondary-900"
            weight="bold"
          >
            Dates
          </BasicText>
          <BasicText
            size="body-m"
            color="secondary-900"
            weight="bold"
            wrap
          >
            Heures attestées
          </BasicText>
          <BasicText
            size="body-m"
            color="secondary-900"
            weight="bold"
            wrap
          >
            Coûts pédagogiques (HT)
          </BasicText>
        </div>
      </template>
      <template #items>
        <div
          v-for="(elem, index) in periodesCoutsStagiaireDetails"
          :key="index"
          class="element__row"
        >
          <BasicText
            size="body-m"
            color="secondary-900"
            wrap
          >
            {{ elem['Prestataire'] }}
          </BasicText>
          <BasicText
            size="body-m"
            color="secondary-900"
            wrap
          >
            {{ elem['Stagiaire']() }}
          </BasicText>
          <BasicText
            size="body-m"
            color="secondary-900"
          >
            {{ elem['Dates'] }}
          </BasicText>
          <BasicText
            size="body-m"
            color="secondary-900"
          >
            {{ elem['HeuresAttestées'] }}
          </BasicText>
          <BasicText
            size="body-m"
            color="secondary-900"
          >
            {{ elem['Couts'] }}
          </BasicText>
        </div>
      </template>
    </SyntheseSection>
    <SyntheseSection
      title="Autres frais (HT)"
      :items="autresFraisDetail"
      class="col-14 col-sm-36"
    />
    <SyntheseSection
      title="Montants (HT)"
      :items="montantsDetail"
      class="col-14 col-sm-36"
    />
    <SyntheseSection
      v-if="estDossier"
      title="Informations du dossier"
      :items="dossierDetail"
      class="col-14 col-sm-36"
    />
    <SyntheseSection
      v-if="estDossier"
      title="Informations de la proforma"
      :items="proformaDetail"
      class="col-14 col-sm-36"
    />
    <SyntheseSection
      v-if="estDossier"
      title="Informations de la facture"
      :items="factureDetail"
      class="col-14 col-sm-36"
    />
  </div>
  <FormContainer>
    <template #content>
      <WrapperCheckbox
        id="field_unknown1"
        v-model="attestationSurHonneurModel"
        :alertLabel="meta.attestationSurHonneur?.$messageErreurDirty!"
        :required="meta.attestationSurHonneur?.$status === PropertyStatus.Required"
        label="J’atteste sur l’honneur l’exactitude des données saisies."
        :readonly
        class="col-36"
      />
      <WrapperCheckbox
        id="field_unknown2"
        v-model="acceptationCgModel"
        :required="meta.acceptationCg?.$status === PropertyStatus.Required"
        :alertLabel="meta.acceptationCg?.$messageErreurDirty!"
        :readonly
        class="col-36"
      >
        <BasicText style="flex: 1">
          J'accepte les Conditions Générales (CG) d'Akto et les conditions de prise en charge, et je confirme avoir pris
          connaissance de celles-ci via le lien suivant : «
          <BasicLink
            label="Conditions générales - Akto"
            @link-click="redirectExterne('https://www.akto.fr/conditions-generales')"
          />
        </BasicText>
        »
      </WrapperCheckbox>
      <div class="form__bloc">
        <WrapperInput
          id="field_240"
          v-model="lieuSignatureModel"
          label="Fait à"
          :required="meta.lieuSignature?.$status === PropertyStatus.Required"
          :alert-label="meta.lieuSignature?.$messageErreurDirty"
          class="col-12 col-md-14 col-sm-36"
          deletable
          hint="50 caractères maximum"
          :maxlength="50"
          :readonly
        />
        <WrapperInput
          v-if="meta.commentaire?.$status !== PropertyStatus.NotPresent"
          id="field_commentaire"
          v-model="commentaireModel"
          label="Commentaires (infos complémentaires...)"
          :required="meta.commentaire?.$status === PropertyStatus.Required"
          :alertLabel="meta.commentaire?.$messageErreurDirty"
          class="col-24 col-md-22 col-sm-36"
          deletable
          hint="256 caractères maximum"
          :maxlength="256"
          :readonly
        />
        <WrapperInput
          v-else
          id="field_commentaire"
          label="Commentaires (infos complémentaires...)"
          class="col-24 col-md-22 col-sm-36"
          hint="256 caractères maximum"
          :maxlength="256"
          disabled
        />
      </div>
    </template>
  </FormContainer>
</template>

<script setup lang="ts">
  import { PropertyStatus } from '@/features/domaine/property-status'
  import { DEMANDE_STORE } from '@/features/interface/demande/shared'
  import { dateFormatter, formatNumber } from '@/features/shared/tools'
  import { redirectExterne } from '@/features/shared/tools/url'
  import { storeToRefs } from 'pinia'
  import { computed, inject } from 'vue'
  import FormContainer from '../../../shared/components/FormContainer.vue'
  import SyntheseSection from '../../../shared/components/SyntheseSection.vue'
  import { StatutFactureLibelle, StatutFactureDTO } from '@/features/api/extranet/dto/Facture/StatutFactureDTO'
  import { type Statut, StatutLibelle } from '@/features/domaine/types/opco/Demande/PROFORMA'

  const store = inject(DEMANDE_STORE)!.getStore<'MODEL_DR'>()
  const { model, meta, readonly, lov } = storeToRefs(store)
  const { setupFieldWithKey } = store

  const lieuSignatureModel = setupFieldWithKey<string>('lieuSignature', true)
  const commentaireModel = setupFieldWithKey<string>('commentaire', true)
  const attestationSurHonneurModel = setupFieldWithKey<boolean>('attestationSurHonneur')
  const acceptationCgModel = setupFieldWithKey<boolean>('acceptationCg')

  const estDossier = computed(() => {
    return model.value?.code
  })

  const periodesCoutsStagiaireDetails = computed(() => {
    return (
      model.value?.attestations
        .map((attestation) => ({
          Prestataire: model.value?.modules.find((module) => module.idModule === attestation.idModule)
            ?.etablissementPrestataire.denomination,
          Stagiaire: () => {
            const stagiaire = model.value?.stagiaires.find(
              (stagiaire) => stagiaire.idIndividuAdhEtablissement === attestation.idSalarie,
            )
            return stagiaire?.prenom + ' ' + stagiaire?.nom
          },
          Dates:
            (attestation.dateDebut ? dateFormatter(attestation.dateDebut) : '') +
            ' - ' +
            (attestation.dateFin ? dateFormatter(attestation.dateFin) : ''),
          HeuresAttestées: attestation.duree ? attestation.duree + 'h' : '-',
          Couts: fraisPedagogiqueModule(
            model.value?.modules.find((module) => module.idModule === attestation.idModule),
          ),
        }))
        .sort((a, b) => a.Prestataire!.localeCompare(b.Prestataire!)) ?? []
    )
  })

  const fraisPedagogiqueModule = (module: any) => {
    const frais = model.value?.frais.find(
      (elem) => elem.idModule === module.idModule && elem.natureFrais === 'Pedagogique',
    )
    const value = frais?.montantDemandeRemboursement ?? 0
    return formatNumber(value, { decimal: 2, separateur: true }) + ' €'
  }

  const autresFraisDetail = computed(() => {
    const list: { [key: string]: any } = {}
    if (model.value?.frais.length) {
      model.value?.frais.forEach((elem) => {
        const key = lov.value.NatureFrais.find((n) => n.code === elem.natureFrais)?.intitule ?? ''
        const value = elem.montantDemandeRemboursement ?? 0
        if (key !== 'Pédagogique') list[key] = Object.keys(list).includes(key) ? list[key] + value : value
      })
    }
    for (const elem in list) list[elem] = formatNumber(list[elem], { decimal: 2, separateur: true }) + ' €'
    return list
  })

  const montantsDetail = computed(() => {
    return {
      'Montant demandé':
        formatNumber(
          model.value?.frais.reduce((sum, module) => sum + (module.montantDemandeRemboursement ?? 0), 0),
          { decimal: 2, separateur: true },
        ) + ' €',
      'Montant accordé':
        formatNumber(
          model.value?.frais.reduce((sum, module) => sum + (module.montantEngage ?? 0), 0),
          { decimal: 2, separateur: true },
        ) + ' €',
      'Montant facturé':
        formatNumber(
          model.value?.frais.reduce((sum, module) => sum + (module.montantRegle ?? 0), 0),
          { decimal: 2, separateur: true },
        ) + ' €',
    }
  })

  const dossierDetail = computed(() => {
    return {
      'Intitule': model.value?.dossier?.intitule,
      'Date de début': dateFormatter(model.value?.dossier?.dateDebut),
      'Date de fin': dateFormatter(model.value?.dossier?.dateFin),
      'Numéro de DRF': model.value?.dossier?.numeroDRF ?? '-',
      // 'Type de demande': ??
    }
  })

  const proformaDetail = computed(() => {
    return {
      'Id': model.value?.proforma?.demandeId,
      'Intitule': model.value?.proforma?.intitule,
      'Statut': StatutLibelle[model.value?.proforma?.statut as Statut],
      'Date de création': dateFormatter(model.value?.proforma?.creationDate)
    }
  })

  const factureDetail = computed(() => {
    return {
      'Id': model.value?.facture?.id,
      'Statut': StatutFactureLibelle[model.value?.facture?.statut as StatutFactureDTO],
      'Code': model.value?.facture?.code,
      'Référence': model.value?.facture?.reference,
      'Type': model.value?.facture?.type ?? '-'
    }
  })
</script>
<style scoped lang="less">
  .synthese__container {
    align-self: center;
    margin: @spacing-20 auto;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-around;
    gap: 20px;
    width: 100%;

    > div {
      width: calc(50% - 10px);
      border-radius: @border-radius-l;

      @media (max-width: @breakpoint-tablet) {
        width: 100%;
      }
    }
  }

  .synthese {
    &__bloc {
      margin: @spacing-20 0;
      display: grid;
      grid-template-columns: repeat(36, 1fr);
      grid-row-gap: @spacing-20;
      grid-column-gap: 2%;
    }
  }

  .element {
    &__header {
      display: flex;
      font-weight: bold;
      padding-bottom: @spacing-10;
      border-bottom: 1px solid @secondary-600;
    }

    &__row {
      flex: 1;
      display: flex;
      padding: 6px 0;
      border-bottom: 1px solid #e0e0e0;
    }

    &__header,
    &__row {
      > span {
        flex: 1;
        padding: 0 @spacing-5;
        text-align: left;
      }
    }
  }
</style>
